import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { toastService } from '../../../../utils/toastService';
import {
    useLazyGetOrderDetailQuery,
    useSubmitForApprovalMutation,
    useUpdateDraftMutation,
    useProcessApprovalMutation,
    useApproveByLogisticsMutation,
    useLazyGetVehicleOrderModelsQuery,
    useLazyGetVehicleOrderMaterialsQuery
} from '../../../../api/endpoints/vehicleOrder';

const useOrderRequestDetail = ({ order, visible, activeTabKey, onActionComplete, brands, paymentTermsList }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();

    const [fetchDetail, { data: detail, isFetching: detailLoading }] = useLazyGetOrderDetailQuery();
    const [submitForApproval, { isLoading: submitting }] = useSubmitForApprovalMutation();
    const [updateDraft, { isLoading: updating }] = useUpdateDraftMutation();
    const [processApproval, { isLoading: processing }] = useProcessApprovalMutation();
    const [approveByLogistics, { isLoading: logisticsProcessing }] = useApproveByLogisticsMutation();

    const [fetchModels, { data: models = [], isFetching: modelsLoading }] = useLazyGetVehicleOrderModelsQuery();
    const [fetchMaterials, { data: materials = [], isFetching: materialsLoading }] = useLazyGetVehicleOrderMaterialsQuery();

    const [brand, setBrand] = useState(null);
    const [model, setModel] = useState(null);
    const [matnr, setMatnr] = useState(null);
    const [maktx, setMaktx] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [deliveryDate, setDeliveryDate] = useState(null);
    const [paymentTerms, setPaymentTerms] = useState(null);
    const [paymentDate, setPaymentDate] = useState(null);

    const [submitted, setSubmitted] = useState(false);
    const [chassisDialogVisible, setChassisDialogVisible] = useState(false);
    const [rejectReturnDialogVisible, setRejectReturnDialogVisible] = useState(false);
    const [rejectReturnAction, setRejectReturnAction] = useState(null);

    useEffect(() => {
        if (visible && order?.id) {
            fetchDetail({ orderId: order.id, languageId });
        }
    }, [visible, order?.id, fetchDetail, languageId]);

    useEffect(() => {
        if (detail) {
            const d = detail;
            setBrand(d.brand || null);
            setModel(d.model || null);
            setMatnr(d.matnr || null);
            setMaktx(d.maktx || '');
            setQuantity(d.quantity || 1);
            setDeliveryDate(d.deliveryDate ? new Date(d.deliveryDate) : null);
            setPaymentTerms(d.paymentTerms || null);
            setPaymentDate(d.paymentDate ? new Date(d.paymentDate) : null);
            setSubmitted(false);

            if (d.brand) fetchModels({ brand: d.brand, languageId });
            if (d.brand && d.model) fetchMaterials({ brand: d.brand, model: d.model, languageId });
        }
    }, [detail, fetchModels, fetchMaterials, languageId]);

    const canEdit = detail?.canEdit ?? false;
    const canApprove = detail?.canApprove ?? false;
    const canReject = detail?.canReject ?? false;
    const canReturn = detail?.canReturn ?? false;
    const requiresChassisSelection = detail?.requiresChassisSelection ?? false;
    const approvalHistory = detail?.approvalHistory ?? [];
    const orderId = detail?.id ?? order?.id;

    const isDraft = activeTabKey === 'draft';
    const isPending = activeTabKey === 'pending';
    const isReadonly = !canEdit;

    const minDate = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const modelOptions = brand ? models : [];
    const materialOptions = brand && model ? materials : [];

    const onBrandChange = useCallback((e) => {
        setBrand(e.value);
        setModel(null);
        setMatnr(null);
        setMaktx('');
        if (e.value) fetchModels({ brand: e.value, languageId });
    }, [fetchModels, languageId]);

    const onModelChange = useCallback((e) => {
        setModel(e.value);
        setMatnr(null);
        setMaktx('');
        if (brand && e.value) fetchMaterials({ brand, model: e.value, languageId });
    }, [brand, fetchMaterials, languageId]);

    const onMaterialChange = useCallback((e) => {
        const value = e.value;
        setMatnr(value);
        const row = materials.find((m) => m.matnr === value);
        setMaktx(row?.maktx ?? '');
    }, [materials]);

    const validate = useCallback(() => {
        if (!brand) return th('vehicleSalesOrderRequests.validation.brandRequired');
        if (!model) return th('vehicleSalesOrderRequests.validation.modelRequired');
        if (!matnr) return th('vehicleSalesOrderRequests.validation.matnrRequired');
        if (!quantity || quantity < 1) return th('vehicleSalesOrderRequests.validation.quantityMin');
        if (!deliveryDate) return th('vehicleSalesOrderRequests.validation.deliveryDateRequired');
        const dDel = new Date(deliveryDate);
        dDel.setHours(0, 0, 0, 0);
        if (dDel < minDate) return th('vehicleSalesOrderRequests.validation.deliveryDatePast');
        if (!paymentTerms) return th('vehicleSalesOrderRequests.validation.paymentTermsRequired');
        if (!paymentDate) return th('vehicleSalesOrderRequests.validation.paymentDateRequired');
        const dPay = new Date(paymentDate);
        dPay.setHours(0, 0, 0, 0);
        if (dPay < minDate) return th('vehicleSalesOrderRequests.validation.paymentDatePast');
        return '';
    }, [brand, model, matnr, quantity, deliveryDate, paymentTerms, paymentDate, minDate, th]);

    const buildBody = useCallback(() => ({
        orderId,
        brand,
        model,
        matnr,
        maktx,
        deliveryDate: deliveryDate instanceof Date ? deliveryDate.toISOString() : deliveryDate,
        quantity: Math.floor(Number(quantity)),
        paymentTerms,
        paymentDate: paymentDate instanceof Date ? paymentDate.toISOString() : paymentDate,
        languageId
    }), [orderId, brand, model, matnr, maktx, deliveryDate, quantity, paymentTerms, paymentDate, languageId]);

    const handleUpdateDraft = useCallback(async () => {
        setSubmitted(true);
        const error = validate();
        if (error) {
            toastService.error(error);
            return;
        }
        try {
            const res = await updateDraft(buildBody()).unwrap();
            if (res?.isSuccessful) {
                toastService.success(res.errorMessage || th('vehicleSalesOrderRequests.messages.draftUpdated'));
                onActionComplete?.();
            } else {
                toastService.error(res?.errorMessage || th('common.error'));
            }
        } catch { /* handled by baseQuery */ }
    }, [validate, buildBody, updateDraft, th, onActionComplete]);

    const handleSubmitForApproval = useCallback(async () => {
        setSubmitted(true);
        const error = validate();
        if (error) {
            toastService.error(error);
            return;
        }
        try {
            const res = await submitForApproval(buildBody()).unwrap();
            if (res?.isSuccessful) {
                toastService.success(res.errorMessage || th('vehicleSalesOrderRequests.messages.submittedForApproval'));
                onActionComplete?.();
            } else {
                toastService.error(res?.errorMessage || th('common.error'));
            }
        } catch { /* handled by baseQuery */ }
    }, [validate, buildBody, submitForApproval, th, onActionComplete]);

    const handleApprove = useCallback(async () => {
        if (requiresChassisSelection) {
            setChassisDialogVisible(true);
            return;
        }

        const body = { orderId, action: 1, languageId };
        if (canEdit) {
            Object.assign(body, buildBody());
            body.orderId = orderId;
        }
        try {
            const res = await processApproval(body).unwrap();
            if (res?.isSuccessful) {
                toastService.success(res.errorMessage || th('vehicleSalesOrderRequests.messages.approved'));
                onActionComplete?.();
            } else {
                toastService.error(res?.errorMessage || th('common.error'));
            }
        } catch { /* handled by baseQuery */ }
    }, [requiresChassisSelection, orderId, canEdit, buildBody, processApproval, th, onActionComplete, languageId]);

    const handleReject = useCallback(() => {
        setRejectReturnAction(2);
        setRejectReturnDialogVisible(true);
    }, []);

    const handleReturn = useCallback(() => {
        setRejectReturnAction(3);
        setRejectReturnDialogVisible(true);
    }, []);

    const handleRejectReturnConfirm = useCallback(async (description) => {
        const body = { orderId, action: rejectReturnAction, description, languageId };
        try {
            const res = await processApproval(body).unwrap();
            if (res?.isSuccessful) {
                const msgKey = rejectReturnAction === 2
                    ? 'vehicleSalesOrderRequests.messages.rejected'
                    : 'vehicleSalesOrderRequests.messages.returned';
                toastService.success(res.errorMessage || th(msgKey));
                setRejectReturnDialogVisible(false);
                onActionComplete?.();
            } else {
                toastService.error(res?.errorMessage || th('common.error'));
            }
        } catch { /* handled by baseQuery */ }
    }, [orderId, rejectReturnAction, processApproval, th, onActionComplete, languageId]);

    const handleChassisApprove = useCallback(async (selectedChassis) => {
        try {
            const res = await approveByLogistics({ orderId, selectedChassis, languageId }).unwrap();
            if (res?.isSuccessful) {
                toastService.success(res.errorMessage || th('vehicleSalesOrderRequests.messages.chassisApproved'));
                setChassisDialogVisible(false);
                onActionComplete?.();
            } else {
                toastService.error(res?.errorMessage || th('common.error'));
            }
        } catch { /* handled by baseQuery */ }
    }, [orderId, approveByLogistics, th, onActionComplete, languageId]);

    const handleQuantityChange = useCallback((e) => {
        const val = e.target.value;
        if (val === '' || val === null || val === undefined) {
            setQuantity('');
            return;
        }
        const parsed = parseInt(val, 10);
        if (!isNaN(parsed) && parsed >= 0) {
            setQuantity(parsed);
        }
    }, []);

    const isActionLoading = submitting || updating || processing || logisticsProcessing;

    return {
        th,
        detail,
        detailLoading,
        brand,
        model,
        matnr,
        maktx,
        quantity,
        deliveryDate,
        paymentTerms,
        paymentDate,
        submitted,
        canEdit,
        canApprove,
        canReject,
        canReturn,
        requiresChassisSelection,
        approvalHistory,
        orderId,
        isDraft,
        isPending,
        isReadonly,
        isActionLoading,
        brands,
        modelOptions,
        materialOptions,
        modelsLoading,
        materialsLoading,
        paymentTermsList,
        minDate,
        onBrandChange,
        onModelChange,
        onMaterialChange,
        setDeliveryDate,
        setPaymentTerms,
        setPaymentDate,
        handleQuantityChange,
        handleUpdateDraft,
        handleSubmitForApproval,
        handleApprove,
        handleReject,
        handleReturn,
        handleRejectReturnConfirm,
        handleChassisApprove,
        chassisDialogVisible,
        setChassisDialogVisible,
        rejectReturnDialogVisible,
        setRejectReturnDialogVisible,
        rejectReturnAction
    };
};

export default useOrderRequestDetail;
